import { useState, useCallback, useEffect } from "react";
import { PlantSearchMenu } from "../PlantSearchMenu/PlantSearchMenu";
import { PlantSearchInput } from "../PlantSearchInput/PlantSearchInput";
import { useVrsDeviceActions } from "../../../actions/vrsDeviceActions";
import { useConfigActions } from "../../../actions/configActions";
import Logger from "../../../utilities/Logger/Logger";
import { useConfigCacheState, useVrsTranslationState } from "../../../context/AppContext/AppContext";
import { useDebounce } from "../../Debouncer/Debouncer";
import { DEBOUNCED_SEARCH_DELAY } from "../../../constants/global";
import { styled } from "@mui/material/styles";
import { Box } from "@mui/system";
import { InfoTooltip } from "../../../components/Basic/InfoTooltip/InfoTooltip";
import _default from "ts-mockito/lib/ts-mockito";

const RootContainer = styled("div")(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  borderRadius: theme.spacing(0.625),
  width: '100%'
}));

const RangeSelectorHeadContainer = styled("div")({
  '&:hover': {
    cursor: 'pointer',
  }
});

const StyledPlantSearchInput = styled(PlantSearchInput)(({ theme }) => ({
  width: '100%',
  [theme.breakpoints.down('md')]: {
    padding: '0 5px'
  }
}));

interface PlantSearchProps {
  plantSearchTerm: string;
  setPlantSearchTerm: (searchTerm) => void;
  onPlantSearchSelectionChanged: (plant: any) => void;
}

function PlantSearch({
  plantSearchTerm,
  setPlantSearchTerm,
  onPlantSearchSelectionChanged,
}: PlantSearchProps) {

  const configActions = useConfigActions();
  const vrsDeviceActions = useVrsDeviceActions();
  const { _T } = useVrsTranslationState();

  const [searchTerm, setSearchTerm] = useState<string>(plantSearchTerm);
  const debouncedFilter = useDebounce(searchTerm, DEBOUNCED_SEARCH_DELAY);

  const [plantSelectionMenu, setPlantSelectionMenu] = useState(null);

  const [searchPlantResult, setSearchPlantResult] = useState<Array<any>>([]);

  const getAppState = useConfigCacheState();

  useEffect(() => {
    setPlantSearchTerm(debouncedFilter as string);
  }, [debouncedFilter, setPlantSearchTerm]);

  const searchPlant = useCallback(
    async (searchTerm, target) => {
      try {
        configActions.setIsLoading(true);
        const cacheKey = `C_${searchTerm}`;
        const cachedPlantsForKey = getAppState("plantSearch", cacheKey);

        const plants: any = cachedPlantsForKey
          ? cachedPlantsForKey
          : await vrsDeviceActions.searchPlantAppSync(searchTerm);
        if (plants !== cachedPlantsForKey) {
          configActions.setAppCache("plantSearch", cacheKey, plants);
        }
        setSearchPlantResult(plants);
        setPlantSelectionMenu(target);
      } catch (error) {
        Logger.of("Search Plants").info("Error searching plants", error);
      } finally {
        configActions.setIsLoading(false);
      }
    },
    [configActions, getAppState, vrsDeviceActions]
  );

  return (
    <RootContainer>
      <RangeSelectorHeadContainer>

        <Box sx={{ position: "relative", display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{position: "absolute", left: '-35px'}} ><InfoTooltip textId={_T("Search by: Company name, Site name, Company ID, Site ID or Printer serial number")} /></Box>
          <StyledPlantSearchInput
            searchTerm={searchTerm}
            onChange={(event) => {
              setSearchTerm(event.target.value);
            }}
            onSubmitSearch={(term, target) => {
              searchPlant(term, target);
            }}
          />
        </Box>
      </RangeSelectorHeadContainer>
      <PlantSearchMenu
        plants={searchPlantResult}
        onPlantSearchSelectionChanged={onPlantSearchSelectionChanged}
        plantSelectionMenu={plantSelectionMenu}
        setPlantSelectionMenu={setPlantSelectionMenu}
      />
    </RootContainer>
  );
}

PlantSearch.displayName = "PlantSearch";

export { PlantSearch };
